// web/src/lib/reviewGate.ts
export type GateRole = 'admin' | 'visual_lead' | 'director' | 'animator';
export type CommentAudience = 'internal' | 'director';
export type GateStage = 'internal' | 'director' | 'relay';
type Role = string | null | undefined;

export const VISUAL_LEAD = 'visual_lead';
export const VISUAL_LEAD_NAME = '視覺統籌';

/** 資料庫的 role + visual_lead → 實際角色（升成製作或導演之後旗標不算數） */
export function effectiveMemberRole(role: Role, visualLead: unknown): string | null {
  if (!role) return null;
  return role === 'animator' && Number(visualLead) === 1 ? VISUAL_LEAD : role;
}
/** 實際角色 → 寫進資料庫的兩個欄位 */
export function memberRoleColumns(role: string): { role: string; visualLead: 0 | 1 } {
  return role === VISUAL_LEAD ? { role: 'animator', visualLead: 1 } : { role, visualLead: 0 };
}
/** 放行、退回、轉交 */
export const canGate = (role: Role) => role === 'admin' || role === VISUAL_LEAD;
/** 這個人看得到哪一邊的留言 */
export function visibleAudiences(role: Role): CommentAudience[] {
  if (role === 'admin' || role === VISUAL_LEAD) return ['internal', 'director'];
  if (role === 'animator') return ['internal'];
  if (role === 'director') return ['director'];
  return [];
}
export const canSeeAudience = (role: Role, audience: string) => (visibleAudiences(role) as string[]).includes(audience);

/** 新留言算哪一邊 */
export function audienceForNewComment(o: {
  role: Role; requested?: unknown; parentAudience?: string | null; onBehalf?: boolean;
}): { ok: true; audience: CommentAudience } | { ok: false; status: number; error: string } {
  const allowed = visibleAudiences(o.role);
  if (o.parentAudience) {
    return allowed.includes(o.parentAudience as CommentAudience)
      ? { ok: true, audience: o.parentAudience as CommentAudience }
      : { ok: false, status: 404, error: '要回覆的留言已經被刪掉了' };
  }
  if (o.onBehalf) return { ok: true, audience: 'director' };
  if (o.requested === 'internal' || o.requested === 'director') {
    return allowed.includes(o.requested)
      ? { ok: true, audience: o.requested }
      : { ok: false, status: 403, error: '沒有權限在這邊留言' };
  }
  if (allowed.length === 0) return { ok: false, status: 403, error: '沒有權限留言' };
  return { ok: true, audience: allowed[0] };   // PM、視覺統籌沒指定（舊版前端）= 內部；導演 = 導演那邊
}

/** 這個人看不看得到這一版（導演：放行過的，或自己交的） */
export function canSeeVideo(role: Role, video: { released_at?: string | null; created_by?: string | null }, userId: string | null | undefined): boolean {
  if (role !== 'director') return !!role;
  return !!video.released_at || (!!userId && video.created_by === userId);
}
/** 這個人看到的歌曲狀態 */
export function songStatusFor(role: Role, song: { status: string; internal_status?: string | null }, hasReleased: boolean): string {
  if (role === 'animator') return song.internal_status || 'todo';
  if (role === 'director') return hasReleased ? song.status : 'todo';
  return song.status;
}
/** 用其他身分看：只有製作可以「降」成導演或動畫師；其他人帶了一律不理 */
export function viewerRoleOf(realRole: Role, header: string | null | undefined): string | null {
  if (!realRole) return null;
  if (realRole === 'admin' && (header === 'director' || header === 'animator')) return header;
  return realRole;
}
/** 動畫師才受指派限制；還沒指派的歌照舊 */
export function songOffLimits(role: Role, song: { animator_user_id?: string | null } | null | undefined, userId: string | null | undefined): boolean {
  return role === 'animator' && !!song?.animator_user_id && song.animator_user_id !== userId;
}
/** 動畫師收不到的通知種類（包含上線前寫進去的舊通知） */
export const ANIMATOR_HIDDEN_NOTICE_KINDS: readonly string[] = ['needs_changes', 'approved', 'on_behalf', 'new_version', 'pm_new_version', 'pm_comment', 'guest_comment', 'released'];
/** 導演收不到的通知種類 */
export const DIRECTOR_HIDDEN_NOTICE_KINDS: readonly string[] = ['pm_new_version', 'pm_comment', 'relayed', 'returned', 'relayed_approved', 'internal_comment'];

/** 活動紀錄的一列這個人看不看得到。releasedIds = 這個專案放行過的版本 id */
export function activityVisibleTo(role: Role, row: { target_type?: string | null; target_id?: string | null; payload?: Record<string, unknown> | null }, releasedIds: ReadonlySet<string>): boolean {
  if (role === 'admin' || role === VISUAL_LEAD) return true;
  const p = row.payload ?? {};
  const statusRow = row.target_type === 'song' && (p.statusTo !== undefined || p.statusFrom !== undefined || (p.changes as { reason?: unknown } | undefined)?.reason !== undefined);
  if (role === 'animator') return !statusRow;
  if (role === 'director') {
    if (row.target_type === 'song_gate' || row.target_type === 'drive_file') return false;
    // 成員、邀請的紀錄都是人名；只改指派的歌也不給（cf-worker/directorPrivacy.mjs 另外清掉混在別的修改裡的指派）
    if (row.target_type === 'member' || row.target_type === 'invite') return false;
    if (row.target_type === 'song' && p.changes && typeof p.changes === 'object' && 'animator_user_id' in p.changes && Object.keys(p.changes).length === 1) return false;
    if (row.target_type === 'song_video') return !!row.target_id && releasedIds.has(row.target_id);
    if (row.target_type === 'song' && (p.internalStatusTo !== undefined || p.auto === true)) return false;
    return true;
  }
  return false;
}

export const GATE_STAGE_LABEL: Record<GateStage, string> = { internal: '內部調整', director: '導演審查中', relay: '待轉交' };
export const IN_PRODUCTION_LABEL = '製作中';
export const GATE_NOT_RELEASED = '這首歌還沒放行，按「給導演看」之後才能決定';
export const VERSION_NOT_RELEASED = '這一版還沒放行，先按「給導演看」';
export const RELEASED_LOCKED = '這一版已經給導演看了，要刪請找 PM';
